import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { FaHome, FaUser, FaComments } from 'react-icons/fa';
import { db, auth } from '../services/firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDocs,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

function BottomNav() {
  const [hasUnread, setHasUnread] = useState(false);


  // If you use serverTimestamp() for lastUpdated/lastRead,
  // this helps avoid flicker by ignoring local pending writes.
  const IGNORE_PENDING_WRITES = true;

  useEffect(() => {
    let unsubscribers = [];
    let unsubscribeAuth;
    let isMounted = true;

    // Shared state for this effect
    const unreadStatus = {};            // { [groupId]: boolean }
    const initialLoadedForGroup = {};   // { [groupId]: boolean }
    let didInitialAggregate = false;



    const aggregate = () => {
      // Aggregate across all groups (booleans only).
      const anyUnread = Object.values(unreadStatus).some(Boolean);
      setHasUnread(anyUnread);
    };

    const tryInitialAggregate = () => {
      // Only aggregate after all groups have completed their initial data load
      const keys = Object.keys(initialLoadedForGroup);
      if (!didInitialAggregate && keys.length > 0 && keys.every(k => initialLoadedForGroup[k])) {
        didInitialAggregate = true;
        aggregate();
      }
    };

    const cleanupGroupListeners = () => {
      // Unsubscribe all current listeners (e.g., when auth user changes)
      unsubscribers.forEach(unsub => unsub && unsub());
      unsubscribers = [];
    };

    unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      // Whenever the user changes, clean up previous listeners
      cleanupGroupListeners();

      if (!user) {
        // No user -> no unread chats
        setHasUnread(false);
        return;
      }

      try {
        // Fetch all groups (activities) for the user
        const activitiesRef = collection(db, 'search_activities');
        const q = query(
          activitiesRef,
          where('ownerId', '==', user.uid),
          where('status', 'in', ['confirmed', 'finished'])
        );

        const snapshot = await getDocs(q);
        if (!isMounted) return; // prevent updates after unmount

        const groupIds = snapshot.docs
          .map(d => d.data()?.groupId)
          .filter(Boolean);


        if (groupIds.length === 0) {
          setHasUnread(false);
          return;
        }

        // Reset shared state
        for (const id of groupIds) {
          unreadStatus[id] = false;            // initialize as boolean
          initialLoadedForGroup[id] = false;   // initial load not done yet
        }
        didInitialAggregate = false;           // reset initial aggregate

        // Subscribe to each group's chat + read docs
        groupIds.forEach((groupId) => {
          const chatRef = doc(db, 'chats', groupId);
          const readRef = doc(db, `chats/${groupId}/chat_reads/${user.uid}`);

          let lastUpdated = null;   // Date or null
          let lastRead = null;      // Date or null
          let hasChatLoaded = false;
          let hasReadLoaded = false;

          const evaluateUnread = (isInitial = false) => {
            if (!hasChatLoaded || !hasReadLoaded) return;

            // Ensure boolean only:
            const isUnread = !!lastUpdated && (!lastRead || lastUpdated > lastRead);
            unreadStatus[groupId] = isUnread;

            if (isInitial) {
              // Mark initial load complete for this group
              initialLoadedForGroup[groupId] = true;
              tryInitialAggregate();
            } else if (didInitialAggregate) {
              // After initial aggregate, update live
              aggregate();
            }
          };

          // Chat snapshot (for lastUpdated)
          const unsubChat = onSnapshot(
            chatRef,
            { includeMetadataChanges: true },
            (chatSnap) => {
              // Optionally skip local/pending writes to avoid flicker
              if (IGNORE_PENDING_WRITES && chatSnap.metadata.hasPendingWrites) return;

              hasChatLoaded = true;

              if (chatSnap.exists()) {
                const data = chatSnap.data();
                // Firestore Timestamp -> Date
                const ts = data?.lastUpdated;
                lastUpdated = typeof ts?.toDate === 'function' ? ts.toDate() : (ts ?? null);
                // If for some reason ts is already a Date, we keep it.
                if (lastUpdated && !(lastUpdated instanceof Date)) {
                  // Normalize if needed
                  lastUpdated = new Date(lastUpdated);
                }
              } else {
                lastUpdated = null;
              }

              const isInitial = !initialLoadedForGroup[groupId];
              evaluateUnread(isInitial);
            }
          );

          // Read snapshot (for lastRead)
          const unsubRead = onSnapshot(
            readRef,
            { includeMetadataChanges: true },
            (readSnap) => {
              if (IGNORE_PENDING_WRITES && readSnap.metadata.hasPendingWrites) return;

              hasReadLoaded = true;

              if (readSnap.exists()) {
                const data = readSnap.data();
                // Firestore Timestamp -> Date
                const ts = data?.lastRead;
                lastRead = typeof ts?.toDate === 'function' ? ts.toDate() : (ts ?? null);
                if (lastRead && !(lastRead instanceof Date)) {
                  lastRead = new Date(lastRead);
                }
              } else {
                lastRead = null;
              }

              const isInitial = !initialLoadedForGroup[groupId];
              evaluateUnread(isInitial);
            }
          );

          unsubscribers.push(unsubChat, unsubRead);
        });
      } catch (error) {
        console.error('Error checking unread messages:', error);
      }
    });

    return () => {
      isMounted = false;
      cleanupGroupListeners();
      if (unsubscribeAuth) unsubscribeAuth();
    };
  }, []); // run once

  return (
    <nav className="navbar fixed-bottom custom-navbar border-top justify-content-around">
      <NavLink
        to="/home"
        className={({ isActive }) => `nav-link text-center ${isActive ? 'active-link' : ''}`}
      >
        <FaHome size={24} />
        <div className="nav-label">Home</div>
      </NavLink>

      <NavLink
        to="/chats"
        className={({ isActive }) => `nav-link text-center ${isActive ? 'active-link' : ''}`}
      >
   
        <div className="nav-item position-relative d-flex flex-column align-items-center">
          <FaComments size={24} />
          {hasUnread && (
            <span
              className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle"
              style={{ width: '15px', height: '15px' }}
            ></span>
          )}

          <div className="nav-label">Chats</div>
        </div>


      </NavLink>

      <NavLink
        to="/useraccount"
        className={({ isActive }) => `nav-link text-center ${isActive ? 'active-link' : ''}`}
      >
        <FaUser size={24} />
        <div className="nav-label">Account</div>
      </NavLink>
    </nav>
  );
}

export default BottomNav;